import React from 'react';

/**
 * The typeahead dropdown. (item 3)
 *
 * WHAT A ROW IS. A document, not a query. Pressing Enter on one navigates to
 * that document; it does not put words in the box. This corpus is addressed by
 * document number, so the person typing "DOE-STD-1104" already knows what they
 * want and the fastest correct response is to hand it to them.
 *
 * WHY EVERY ROW SHOWS ITS STATUS. DOE-STD-1104 exists as -2009, -2014 and
 * -2016, and only one of those is in force. A picker that lists three
 * identical titles and lets the user guess is worse than no picker, because it
 * looks authoritative. The document number and the status are therefore not
 * decoration — they are what makes a row decidable. (They are also why
 * duplicate-looking titles are left in rather than collapsed: the repeats ARE
 * the version family, which is the thing this archive exists to preserve.)
 *
 * ACCESSIBILITY. This is the ARIA 1.2 combobox pattern, which is prescriptive
 * about a detail that is easy to get wrong: focus never leaves the text input.
 * Arrow keys move `aria-activedescendant` between option ids while the input
 * keeps DOM focus, so a screen reader announces the highlighted row and the
 * user can keep typing at any point. Rows respond to `onMouseDown` rather than
 * `onClick` because `onClick` fires after the input's `blur`, by which time
 * the panel has already closed and the click lands on nothing.
 */

/** Split a label on the typed text so the matched part can be marked. Plain
 *  substring, case-insensitive: the suggestion may have matched on a field
 *  that is not being displayed, in which case nothing is marked, which is
 *  honest — highlighting a span that did not cause the match is a lie. */
const mark = (text, needle) => {
  const value = String(text ?? '');
  const q = (needle || '').trim();
  if (!q) return value;
  const at = value.toLowerCase().indexOf(q.toLowerCase());
  if (at < 0) return value;
  return (
    <>
      {value.slice(0, at)}
      <mark>{value.slice(at, at + q.length)}</mark>
      {value.slice(at + q.length)}
    </>
  );
};

const STATUS_MODIFIER = (status) =>
  String(status || '')
    .toLowerCase()
    .replace(/[^a-z]+/g, '-');

const SuggestPanel = ({
  id,
  items,
  term,
  activeIndex,
  onPick,
  showSite,
  listRef,
}) => {
  if (!items?.length) return null;

  return (
    <ul
      className="suggest-panel"
      id={id}
      role="listbox"
      aria-label="Document suggestions"
      ref={listRef}
    >
      {items.map((item, i) => {
        const active = i === activeIndex;
        return (
          /* eslint-disable-next-line jsx-a11y/click-events-have-key-events --
             keyboard handling lives on the input, per the combobox pattern. */
          <li
            key={`${item.site}:${item.url}`}
            id={`${id}-option-${i}`}
            role="option"
            aria-selected={active}
            className={`suggest-panel__row${active ? ' is-active' : ''}`}
            // See header: onClick would fire after blur has closed the panel.
            onMouseDown={(e) => {
              e.preventDefault();
              onPick(item);
            }}
          >
            <span className="suggest-panel__main">
              {item.docid && (
                <span className="suggest-panel__docid">
                  {mark(item.docid, term)}
                </span>
              )}
              <span className="suggest-panel__title">
                {mark(item.title, term)}
              </span>
            </span>
            <span className="suggest-panel__meta">
              {showSite && item.site_label && (
                <span className="suggest-panel__site">{item.site_label}</span>
              )}
              {item.status && (
                <span
                  className={`suggest-panel__status is-${STATUS_MODIFIER(item.status)}`}
                >
                  {item.status}
                </span>
              )}
            </span>
          </li>
        );
      })}
    </ul>
  );
};

export default SuggestPanel;
